import React, { useCallback, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Input, version2 } from 'ui-components/components/input';
import { InputLabel } from '../../../address/input-label';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { InlineMessage } from 'ui-components/components/inline-message';

import Global from '../../../store/global-selectors';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { Segment } from '../../../services/analytics-services/segment';
import { AUTOMATION_RECOMMENDATION_CONTACT_FORM_LOADED } from '../../../constants/analytics-events';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useAutomationRecommendationsContext } from '../automation-recommendations-context';
import { useYupFormValidation } from '../../../validation/use-yup-form-validation';
import { useContactSalesValidationSchema } from './validation-schema';
import style from './style.module.less';
import messages from './messages';
import { LogService } from '../../../services/log-service';
import { SimpleModal } from '../../../components/simple-modal';

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {int} props.recommendationId
 * @param {() => void} props.onSuccess
 * @returns {JSX.Element}
 */
export const ContactSalesModal = ({ onClose, recommendationId, onSuccess }) => {
  const { setSalesHasBeenContacted } = useAutomationRecommendationsContext();
  const currentUser = useSelector(Global.selectUserCurrentUserInfo);
  const [name, setName] = useState(currentUser.name);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState(currentUser.email);
  const [loading, setLoading] = useAsyncSafeState(false);
  const [error, setError] = useAsyncSafeState('');
  const [touchedFields, setTouchedFields] = useState({});
  const contactSalesValidationSchema = useContactSalesValidationSchema();

  const form = useMemo(() => ({ name, phoneNumber, email }), [name, phoneNumber, email]);
  const { errors: validationErrors, isValid } = useYupFormValidation({
    form,
    schema: contactSalesValidationSchema,
  });

  const markFieldAsTouched = useCallback((fieldName) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  useOnComponentMount(() => {
    try {
      Segment.trackEvent(AUTOMATION_RECOMMENDATION_CONTACT_FORM_LOADED, {
        currentUser,
      });
    } catch (e) {
      LogService.logError(new Error('Could not contact segment', { cause: e }));
    }
  });

  const save = useCallback(async () => {
    if (!isValid) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`/automationRecommendations/accept/${recommendationId}`, {
        fullName: name,
        email,
        phoneNumber,
      });
      if (response?.data?.success) {
        setSalesHasBeenContacted(true);
        onClose();
        onSuccess();
        return;
      }
      setLoading(false);
      setError(response?.data);
    } catch (e) {
      setLoading(false);
      setError(e?.message);
    }
  }, [email, isValid, name, onClose, onSuccess, phoneNumber, recommendationId, setError, setLoading, setSalesHasBeenContacted]);

  return (
    <SimpleModal
      actionMessage={messages.saveButton}
      headerMessage={messages.modalTitle}
      isActionDisabled={!isValid || loading || error}
      onAction={save}
      onClose={onClose}
      onSecondAction={onClose}
      secondActionMessage={messages.cancelButton}
      size='medium'
    >
      <FlexColumn className={style.body}>
        {loading && (
          <div className={style['loading-indicator']}>
            <LoadingIndicator />
          </div>
        )}
        {!loading && error && <Text className={style.red} textType={TextType.Paragraph} value={messages.secondaryTitle} />}
        {!loading && !error && (
          <>
            <Text textType={TextType.H3} value={messages.secondaryTitle} />
            <Text textType={TextType.Paragraph} value={messages.description} />
            <FlexRow className={style.row}>
              <div className={style.half}>
                <InputLabel hasError={!!(validationErrors.name && touchedFields.name)} labelFor='name-input' required>
                  <FormattedMessage {...messages.name} />
                </InputLabel>
                <Input
                  className={style['full-width-input']}
                  id='name-input'
                  onBlur={() => markFieldAsTouched('name')}
                  onChange={(e) => setName(e.target.value)}
                  type='text'
                  value={name}
                  version={version2}
                />
                {validationErrors.name && touchedFields.name && <InlineMessage type='error'>{validationErrors.name.message}</InlineMessage>}
              </div>
              <div className={style.half} />
            </FlexRow>
            <FlexRow className={style.row}>
              <div className={style.half}>
                <InputLabel hasError={!!(validationErrors.phoneNumber && touchedFields.phoneNumber)} labelFor='phone-number-input' required>
                  <FormattedMessage {...messages.phoneNumber} />
                </InputLabel>
                <Input
                  className={style['full-width-input']}
                  id='phone-number-input'
                  onBlur={() => markFieldAsTouched('phoneNumber')}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  type='text'
                  value={phoneNumber}
                  version={version2}
                />
                {validationErrors.phoneNumber && touchedFields.phoneNumber && (
                  <InlineMessage type='error'>{validationErrors.phoneNumber.message}</InlineMessage>
                )}
              </div>
              <div className={style.half}>
                <InputLabel hasError={!!(validationErrors.email && touchedFields.email)} labelFor='email-input' required>
                  <FormattedMessage {...messages.email} />
                </InputLabel>
                <Input
                  className={style['full-width-input']}
                  id='email-input'
                  onBlur={() => markFieldAsTouched('email')}
                  onChange={(e) => setEmail(e.target.value)}
                  type='text'
                  value={email}
                  version={version2}
                />
                {validationErrors.email && touchedFields.email && (
                  <InlineMessage type='error'>{validationErrors.email.message}</InlineMessage>
                )}
              </div>
            </FlexRow>
          </>
        )}
      </FlexColumn>
    </SimpleModal>
  );
};
